import { Link } from 'react-router-dom';
import { Heart, Users, TrendingUp, Award } from 'lucide-react';

const About = () => {
  return (
    <div className="bg-off_white" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand_blue to-brand_red py-20 border-b-4 border-black relative overflow-hidden">
        {/* Decorative Stars */}
        <div className="absolute top-10 right-20 w-10 h-10">
          <div className="w-full h-full bg-brand_yellow/80 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        </div>
        <div className="absolute bottom-16 left-16 w-8 h-8">
          <div className="w-full h-full bg-white/70 rotate-45" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="absolute top-1/3 left-1/4 w-6 h-6">
          <div className="w-full h-full bg-brand_green/60 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '10s' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="font-chewy text-5xl sm:text-7xl text-white drop-shadow-[4px_4px_0px_rgba(0,0,0,1)] mb-6">
            OUR STORY
          </h1>
          <p className="font-nunito text-xl text-white font-bold">
            More Than Just Lemonade - A Mission to Empower Families
          </p>
        </div>
      </section>

      {/* The Story */}
      <section className="py-16 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-b-4 border-black relative overflow-hidden">
        {/* More Stars */}
        <div className="absolute top-16 left-12 w-10 h-10">
          <div className="w-full h-full bg-amber-400/60 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '2.5s' }} />
        </div>
        <div className="absolute top-24 right-20 w-8 h-8">
          <div className="w-full h-full bg-orange-400/70 rotate-12" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="absolute bottom-20 left-24 w-9 h-9">
          <div className="w-full h-full bg-yellow-400/80 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '10s' }} />
        </div>
        <div className="absolute bottom-32 right-16 w-7 h-7">
          <div className="w-full h-full bg-amber-300/70 rotate-45 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)', animationDuration: '3s' }} />
        </div>
        <div className="absolute top-1/3 left-1/4 w-6 h-6">
          <div className="w-full h-full bg-orange-500/60 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '14s' }} />
        </div>
        <div className="absolute top-1/2 right-1/3 w-8 h-8">
          <div className="w-full h-full bg-yellow-300/80 rotate-12" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-off_white border-4 border-black rounded-2xl p-8 shadow-hard">
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_uncle-dave-lemonade/artifacts/kwt5a30n_UNCLE_20250926_111530_0000.png"
                alt="Uncle Dave's Characters"
                className="w-full max-w-2xl mx-auto"
              />
            </div>
            
            <div className="space-y-6 font-nunito text-lg leading-relaxed">
              <p>
                Once upon a time in the vibrant streets of New York City, there lived a man with many nieces and nephews named <span className="font-bold text-brand_blue">Uncle Dave</span>. Growing up, Uncle Dave had a humble lemonade stand that taught him the value of hard work and financial literacy. As he grew older, he realized the importance of passing on this knowledge to others.
              </p>
              
              <p>
                Inspired by his own experiences, Uncle Dave decided to create a brand that would <span className="font-bold text-brand_red">empower single and non-working parent households</span>. Uncle Dave's All Natural Juice was born with a mission to bring families together and offer them a chance to learn about financial independence, while enjoying delicious all-natural juice.
              </p>
              
              <p>
                Uncle Dave's vision was simple: to provide parents and their children with the opportunity to <span className="font-bold text-brand_green">run their own pop-up juice stands</span>. By renting these stands and receiving daily, weekly, or monthly juice supplies, families could earn a percentage of their sales which in return teaches valuable lessons in entrepreneurship.
              </p>
              
              <p>
                Through this initiative, Uncle Dave not only aimed to support families financially but also to foster connections within communities, encouraging parents and children to step out, network, and grow together.
              </p>
              
              <div className="text-center mt-8 pt-8 border-t-4 border-black">
                <p className="font-chewy text-3xl text-brand_blue mb-2">
                  Uncle Dave's Lemonade
                </p>
                <p className="font-chewy text-2xl text-brand_red">
                  "The Best Lemonade Ever Made"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 bg-gradient-to-br from-rose-100 via-pink-100 to-purple-100 relative overflow-hidden">
        {/* More Stars */}
        <div className="absolute top-10 left-16 w-10 h-10">
          <div className="w-full h-full bg-rose-400/70 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '2s' }} />
        </div>
        <div className="absolute top-24 right-24 w-8 h-8">
          <div className="w-full h-full bg-pink-400/60 rotate-12" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="absolute bottom-24 left-20 w-9 h-9">
          <div className="w-full h-full bg-purple-400/80 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '11s' }} />
        </div>
        <div className="absolute bottom-16 right-20 w-7 h-7">
          <div className="w-full h-full bg-rose-300/70 rotate-45 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)', animationDuration: '2.5s' }} />
        </div>
        <div className="absolute top-1/2 left-1/3 w-6 h-6">
          <div className="w-full h-full bg-pink-500/60 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '13s' }} />
        </div>
        <div className="absolute top-1/4 right-1/4 w-8 h-8">
          <div className="w-full h-full bg-purple-300/80 rotate-12" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="font-chewy text-4xl sm:text-6xl text-center mb-12">
            <span className="text-brand_blue">OUR</span>{' '}
            <span className="text-brand_red">VALUES</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Heart,
                title: 'Family First',
                desc: 'Bringing families together through entrepreneurship and quality time'
              },
              {
                icon: Users,
                title: 'Community',
                desc: 'Building connections and networks within neighborhoods'
              },
              {
                icon: TrendingUp,
                title: 'Financial Literacy',
                desc: 'Teaching valuable business skills and money management'
              },
              {
                icon: Award,
                title: 'Quality',
                desc: 'All natural ingredients, no compromises on taste or health'
              }
            ].map((value, idx) => (
              <div
                key={idx}
                className="bg-white border-4 border-black rounded-xl p-6 text-center shadow-hard hover:shadow-hard-lg hover:-translate-y-2 transition-all"
              >
                <div className="bg-brand_blue w-20 h-20 rounded-full border-4 border-black mx-auto mb-4 flex items-center justify-center">
                  <value.icon className="w-10 h-10 text-white" strokeWidth={3} />
                </div>
                <h3 className="font-chewy text-2xl text-brand_blue mb-2">
                  {value.title}
                </h3>
                <p className="font-nunito text-gray-700 font-semibold">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand_blue border-t-4 border-black relative overflow-hidden">
        {/* Decorative Stars */}
        <div className="absolute top-12 left-20 w-9 h-9">
          <div className="w-full h-full bg-brand_yellow/70 rotate-12" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        </div>
        <div className="absolute bottom-20 right-24 w-7 h-7">
          <div className="w-full h-full bg-white/60 animate-pulse" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
        </div>
        <div className="absolute top-1/2 right-16 w-6 h-6">
          <div className="w-full h-full bg-brand_red/50 animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '15s' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-chewy text-4xl sm:text-6xl text-white mb-6 drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]">
            READY TO JOIN THE FAMILY?
          </h2>
          <p className="font-nunito text-xl text-white mb-8 font-semibold">
            Whether you're looking to start a pop-up stand or just enjoy our delicious lemonade,
            <br />Uncle Dave's is here for you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/popup-program"
              className="inline-block bg-brand_yellow text-black font-chewy text-xl px-8 py-4 rounded-full border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all"
            >
              START A POP-UP STAND
            </Link>
            <Link
              to="/products"
              className="inline-block bg-brand_red text-white font-chewy text-xl px-8 py-4 rounded-full border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all"
            >
              SHOP PRODUCTS
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;